import React, { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { Clock, Ship, Package, CircleDot, Search, Download, ChevronDown } from "lucide-react";
import apiClient from "../../services/api";
import ShipmentDetailsPopup from "./ShipmentDetailsPopup";

/** ─── Design tokens (matching TariffCalculator) ─── */
const TEXT_PRIMARY = "#111111";
const TEXT_MUTED   = "#6B7280";
const BORDER_COLOR = "#EAEAEA";
const BG_GREY      = "#F7F7F7";
const FONT         = "'Inter', sans-serif";

/** ─── Shared inline style objects ─── */
const fieldStyle: React.CSSProperties = {
  width: "100%",
  borderRadius: 8,
  border: `1px solid ${BORDER_COLOR}`,
  background: "#fff",
  padding: "0 12px",
  height: 40,
  fontSize: "0.875rem",
  color: TEXT_PRIMARY,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: FONT,
};

const filterBtnStyle: React.CSSProperties = {
  height: 40,
  borderRadius: 8,
  border: `1px solid ${BORDER_COLOR}`,
  background: "#fff",
  padding: "0 12px",
  fontSize: "0.875rem",
  color: TEXT_PRIMARY,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
  outline: "none",
  fontFamily: FONT,
  flexShrink: 0,
};

const dropdownPanelStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  background: "#fff",
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  zIndex: 1000,
  maxHeight: 240,
  overflowY: "auto",
  minWidth: 160,
};

const dropdownItem = (active: boolean): React.CSSProperties => ({
  padding: "8px 12px",
  fontSize: "0.875rem",
  cursor: "pointer",
  background: active ? "#374151" : "transparent",
  color: active ? "#fff" : TEXT_PRIMARY,
  fontFamily: FONT,
  userSelect: "none",
});

interface OrdersOverviewProps {
  loading?: boolean;
  error?: string | null;
  initialStatus?: string;
  initialRisk?: string;
}

interface KpiRow {
  label: string;
  count: number;
  total_value_usd: number;
  // Backend-provided breakdown for the AT_RISK entry (see databricks_utils.get_kpis).
  at_risk_current?: number;
  at_risk_future?: number;
}

const KPI_CONFIG = [
  { key: "NOT_STARTED", display: "Pending",     Icon: Clock,    iconColor: "#2563EB", iconBg: "#EFF6FF", border: "#BFDBFE" },
  { key: "IN_PROGRESS", display: "In Progress", Icon: Ship,     iconColor: "#7C3AED", iconBg: "#F5F3FF", border: "#DDD6FE" },
  { key: "AT_RISK",     display: "At Risk",     Icon: Package,  iconColor: "#EA580C", iconBg: "#FFF7ED", border: "#FED7AA" },
  { key: "DELAYED",     display: "Delayed",     Icon: CircleDot, iconColor: "#DC2626", iconBg: "#FEF2F2", border: "#FECACA" },
] as const;

const STATUS_OPTIONS = [
  { value: "NOT_STARTED",       label: "Pending"               },
  { value: "IN_PROGRESS",       label: "In Progress"           },
  { value: "AT_RISK",           label: "At Risk"               },
  { value: "AT_RISK_CURRENT",   label: "At Risk - Current",  indent: true },
  { value: "AT_RISK_FUTURE",    label: "At Risk - Upcoming", indent: true },
  { value: "DELAYED",           label: "Delayed"               },
  { value: "COMPLETED",         label: "Completed"             },
] as { value: string; label: string; indent?: boolean }[];

const RISK_OPTIONS = [
  { value: "at_risk", label: "At Risk"  },
  { value: "none",    label: "No Risk"  },
];

const OrdersOverview: React.FC<OrdersOverviewProps> = ({
  loading = false,
  error   = null,
  initialStatus,
  initialRisk,
}) => {
  const [kpis,       setKpis]       = useState<KpiRow[]>([]);
  const [kpisLoading, setKpisLoading] = useState(true);

  const [filterStatus,  setFilterStatus]  = useState(initialStatus ?? "");
  const [filterRisk,    setFilterRisk]    = useState(initialRisk ?? "");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchWarning, setSearchWarning] = useState(false);

  const [kpiRows,     setKpiRows]    = useState<any[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [page,        setPage]        = useState(0);
  const ROWS_PER_PAGE = 10;

  // Dropdown open states
  const [statusOpen, setStatusOpen] = useState(false);
  const [riskOpen,   setRiskOpen]   = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);
  const riskRef   = useRef<HTMLDivElement>(null);

  // Sync filter state when navigating from dashboard KPI cards
  useEffect(() => { setFilterStatus(initialStatus ?? ""); }, [initialStatus]);
  useEffect(() => { setFilterRisk(initialRisk ?? ""); }, [initialRisk]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
      if (riskRef.current   && !riskRef.current.contains(e.target as Node))   setRiskOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch KPI counts
  useEffect(() => {
    setKpisLoading(true);
    apiClient
      .get<{ kpis: KpiRow[]; total: number }>("/api/databricks/dashboard/kpis")
      .then((res) => setKpis(res.data.kpis))
      .catch((e) => console.warn("Failed to load KPIs", e))
      .finally(() => setKpisLoading(false));
  }, []);

  // Load rows. AT_RISK_CURRENT / AT_RISK_FUTURE use dedicated backend
  // endpoints (server-side scope filtering); every other status uses /ALL
  // and is filtered client-side along with search and risk.
  useEffect(() => {
    const group =
      filterStatus === "AT_RISK_CURRENT" || filterStatus === "AT_RISK_FUTURE"
        ? filterStatus
        : "ALL";
    setRowsLoading(true);
    apiClient
      .get<{ rows: any[] }>(`/api/databricks/dashboard/kpi-rows/${group}`)
      .then((res) => setKpiRows(res.data.rows ?? []))
      .catch((e) => { console.warn("Failed to load rows", e); setKpiRows([]); })
      .finally(() => setRowsLoading(false));
    setPage(0);
  }, [filterStatus]);

  // Client-side filtering + sort. AT_RISK_CURRENT / AT_RISK_FUTURE are
  // pre-filtered server-side, so for those the status check is a no-op and
  // only search + risk filters apply here.
  const filteredRows = kpiRows.filter((row) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || [row.shipment_id, row.product_name, row.supplier_name, row.location_name, row.destination_location]
      .some((f) => (f || "").toLowerCase().includes(q));
    const rowGroup = (row.current_ui_group || "").toUpperCase().replaceAll(/[\s-]+/g, "_");
    const serverFiltered = filterStatus === "AT_RISK_CURRENT" || filterStatus === "AT_RISK_FUTURE";
    const matchesStatus = !filterStatus || serverFiltered ||
      (filterStatus === "ACTIVE"
        ? ["PENDING", "NOT_STARTED", "IN_PROGRESS", "AT_RISK", "DELAYED"].includes(rowGroup)
        : rowGroup === filterStatus);
    const isAtRisk = ["AT_RISK", "DELAYED"].includes(rowGroup);
    const matchesRisk  = !filterRisk ||
      (filterRisk === "at_risk" && isAtRisk) ||
      (filterRisk === "none"    && !isAtRisk);
    return matchesSearch && matchesStatus && matchesRisk;
  }).sort((a, b) => {
    const ta = a.last_event_ts ? new Date(a.last_event_ts).getTime() : 0;
    const tb = b.last_event_ts ? new Date(b.last_event_ts).getTime() : 0;
    return tb - ta;
  });

  const visibleRows  = filteredRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  const totalPages   = Math.ceil(filteredRows.length / ROWS_PER_PAGE);
  const startRow     = filteredRows.length === 0 ? 0 : page * ROWS_PER_PAGE + 1;
  const endRow       = Math.min((page + 1) * ROWS_PER_PAGE, filteredRows.length);

  // Export as CSV
  const handleExport = () => {
    const headers = ["Shipment ID", "Product", "Supplier", "Current Location", "Destination", "Delay Reason"];
    const csvRows = filteredRows.map((r) => [
      r.shipment_id || "", r.product_name || "", r.supplier_name || "",
      r.delay_reason || "", r.location_name || "", r.destination_location || "",
    ]);
    const csv  = [headers, ...csvRows].map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "orders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 64 }}>
        <CircularProgress size={48} style={{ color: "#111111" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16, backgroundColor: "#F7F7F7", borderRadius: 8, border: "1px solid #EAEAEA" }}>
        <p style={{ color: "#DC3545", fontWeight: 500, fontFamily: FONT, margin: 0 }}>
          Error loading orders data: {error}
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, fontFamily: FONT }}>

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 600, color: TEXT_PRIMARY, margin: 0, fontFamily: FONT, lineHeight: 1 }}>
            Orders
          </h1>
          <Tooltip title="Live shipment data synced from your supply chain" arrow placement="right">
            <span style={{ cursor: "pointer", color: TEXT_MUTED, lineHeight: 1, display: "flex", alignItems: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </span>
          </Tooltip>
        </div>
        <p style={{ fontSize: "0.875rem", color: TEXT_MUTED, margin: 0, fontFamily: FONT }}>
          Monitor and manage your supply chain orders
        </p>
      </div>

      {/* ── Operational Status Summary ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 16px 0", fontFamily: FONT }}>
          Operational Status Summary
        </h2>
        {kpisLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress size={32} style={{ color: "#111111" }} />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {KPI_CONFIG.map(({ key, display, Icon, iconColor, iconBg, border }) => {
              const data  = kpis.find((k) => k.label.toUpperCase().replaceAll(/[\s-]+/g, "_") === key);
              const count = data?.count ?? 0;
              const isActive = key === "AT_RISK"
                ? ["AT_RISK", "AT_RISK_CURRENT", "AT_RISK_FUTURE"].includes(filterStatus)
                : filterStatus === key;
              // AT_RISK current/upcoming counts come directly from the backend
              // KPI payload (databricks_utils.get_kpis injects them on AT_RISK).
              const atRiskBreakdown =
                key === "AT_RISK" && data &&
                ((data.at_risk_current ?? 0) + (data.at_risk_future ?? 0)) > 0
                  ? { current: data.at_risk_current ?? 0, future: data.at_risk_future ?? 0 }
                  : null;
              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setFilterStatus(isActive ? "" : key); setFilterRisk(""); setPage(0); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterStatus(isActive ? "" : key); setFilterRisk(""); setPage(0); } }}
                  style={{
                    background: isActive ? iconBg : "#fff",
                    borderRadius: 12,
                    border: isActive ? `2px solid ${iconColor}` : `1px solid ${border}`,
                    padding: isActive ? 15 : 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    boxShadow: isActive ? `0 0 0 3px ${iconColor}22` : "0 1px 3px rgba(0,0,0,0.06)",
                    cursor: "pointer",
                    transition: "border 0.15s, box-shadow 0.15s, background 0.15s",
                  }}
                  onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 12px rgba(0,0,0,0.10)"; }}
                  onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLDivElement).style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon size={20} style={{ color: iconColor }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, marginBottom: 2, fontFamily: FONT }}>
                      {display}
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1, fontFamily: FONT }}>
                      {count.toLocaleString()}
                    </div>
                    {atRiskBreakdown && (
                      <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginTop: 4, fontFamily: FONT, lineHeight: 1 }}>
                        {atRiskBreakdown.current} current · {atRiskBreakdown.future} upcoming
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Search & Filters ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>

        {/* Search */}
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <div style={{ position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: TEXT_MUTED, pointerEvents: "none" }} />
            <input
              type="text"
              placeholder="Search by shipment ID or location"
              value={searchQuery}
              onChange={(e) => {
                const raw = e.target.value;
                // Allow alphanumeric, spaces, hyphens, underscores, dots, and commas
                const cleaned = raw.replace(/[^a-zA-Z0-9 \-_.,]/g, "");
                const hadSpecial = cleaned.length < raw.length;
                setSearchWarning(hadSpecial);
                if (hadSpecial) {
                  // Clear the warning after 2 seconds
                  setTimeout(() => setSearchWarning(false), 2000);
                }
                setSearchQuery(cleaned);
                setPage(0);
              }}
              style={{ ...fieldStyle, paddingLeft: 40 }}
              onFocus={(e) => { e.currentTarget.style.border = "1px solid #EF2D3E"; e.currentTarget.style.boxShadow = "0 0 0 2px #EF2D3E, 0 0 0 5px rgba(239,45,62,0.12)"; }}
              onBlur={(e)  => { e.currentTarget.style.border = `1px solid ${BORDER_COLOR}`; e.currentTarget.style.boxShadow = "none"; }}
            />
          </div>
          {searchWarning && (
            <p style={{ margin: "4px 0 0 0", fontSize: "0.75rem", color: "#DC2626", fontFamily: FONT }}>
              Special characters are not allowed in search.
            </p>
          )}
        </div>

        {/* All Status dropdown */}
        <div ref={statusRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => { setStatusOpen((o) => !o); setRiskOpen(false); }}
            style={{
              ...filterBtnStyle,
              border: statusOpen ? "1px solid #EF2D3E" : `1px solid ${BORDER_COLOR}`,
              boxShadow: statusOpen ? "0 0 0 2px #EF2D3E, 0 0 0 5px rgba(239,45,62,0.12)" : "none",
              minWidth: 140,
            }}
          >
            <span style={{ color: filterStatus ? TEXT_PRIMARY : TEXT_MUTED }}>
              {filterStatus ? STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label : "All Status"}
            </span>
            <ChevronDown size={14} style={{ color: TEXT_MUTED, transform: statusOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", marginLeft: "auto" }} />
          </button>
          {statusOpen && (
            <div style={dropdownPanelStyle}>
              <button
                type="button"
                onMouseDown={() => { setFilterStatus(""); setStatusOpen(false); setPage(0); }}
                style={{ ...dropdownItem(filterStatus === ""), width: "100%", textAlign: "left", border: "none" }}
                onMouseEnter={(e) => { if (filterStatus !== "") (e.currentTarget as HTMLButtonElement).style.background = BG_GREY; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = filterStatus === "" ? "#374151" : "transparent"; }}
              >
                All Status
              </button>
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onMouseDown={() => { setFilterStatus(o.value); setStatusOpen(false); setPage(0); }}
                  style={{
                    ...dropdownItem(filterStatus === o.value),
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    paddingLeft: o.indent ? 28 : 12,
                    fontSize: o.indent ? "0.8125rem" : "0.875rem",
                  }}
                  onMouseEnter={(e) => { if (filterStatus !== o.value) (e.currentTarget as HTMLButtonElement).style.background = BG_GREY; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = filterStatus === o.value ? "#374151" : "transparent"; }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* All Risk Levels dropdown */}
        <div ref={riskRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => { setRiskOpen((o) => !o); setStatusOpen(false); }}
            style={{
              ...filterBtnStyle,
              border: riskOpen ? "1px solid #EF2D3E" : `1px solid ${BORDER_COLOR}`,
              boxShadow: riskOpen ? "0 0 0 2px #EF2D3E, 0 0 0 5px rgba(239,45,62,0.12)" : "none",
              minWidth: 156,
            }}
          >
            <span style={{ color: filterRisk ? TEXT_PRIMARY : TEXT_MUTED }}>
              {filterRisk ? RISK_OPTIONS.find((o) => o.value === filterRisk)?.label : "All Risk Levels"}
            </span>
            <ChevronDown size={14} style={{ color: TEXT_MUTED, transform: riskOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", marginLeft: "auto" }} />
          </button>
          {riskOpen && (
            <div style={dropdownPanelStyle}>
              <button
                type="button"
                onMouseDown={() => { setFilterRisk(""); setRiskOpen(false); setPage(0); }}
                style={{ ...dropdownItem(filterRisk === ""), width: "100%", textAlign: "left", border: "none" }}
                onMouseEnter={(e) => { if (filterRisk !== "") (e.currentTarget as HTMLButtonElement).style.background = BG_GREY; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = filterRisk === "" ? "#374151" : "transparent"; }}
              >
                All Risk Levels
              </button>
              {RISK_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onMouseDown={() => { setFilterRisk(o.value); setRiskOpen(false); setPage(0); }}
                  style={{ ...dropdownItem(filterRisk === o.value), width: "100%", textAlign: "left", border: "none" }}
                  onMouseEnter={(e) => { if (filterRisk !== o.value) (e.currentTarget as HTMLButtonElement).style.background = BG_GREY; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = filterRisk === o.value ? "#374151" : "transparent"; }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Export */}
        <button
          type="button"
          onClick={handleExport}
          style={{ ...filterBtnStyle, gap: 8 }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = BG_GREY; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#fff"; }}
        >
          <Download size={15} style={{ color: TEXT_MUTED }} />
          <span style={{ fontWeight: 500 }}>Export</span>
        </button>
      </div>

      {/* ── Orders Table ── */}
      <div style={{ background: "#fff", borderRadius: 8, border: `1px solid ${BORDER_COLOR}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {rowsLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <CircularProgress size={32} style={{ color: "#111111" }} />
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
              <thead>
                <tr style={{ backgroundColor: BG_GREY, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  {["Shipment ID", "Product", "Supplier", "Current Location", "Destination", filterStatus === "AT_RISK_FUTURE" ? "Risk Reason" : "Delay Reason"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "12px 24px",
                        textAlign: "left",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: TEXT_MUTED,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                        fontFamily: FONT,
                        borderBottom: `1px solid ${BORDER_COLOR}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, idx) => {
                  const rowGroup = (row.current_ui_group || "").toUpperCase().replaceAll(/[\s-]+/g, "_");
                  const portScope = (row.port_risk_scope || "").toString().toUpperCase();
                  const isCurrentRisk = rowGroup === "AT_RISK" && portScope === "CURRENT";
                  const isFutureRisk = rowGroup === "AT_RISK" && portScope === "FUTURE";
                  const delayColor = !row.delay_reason
                    ? TEXT_MUTED
                    : isFutureRisk
                      ? "#B45309"
                      : "#DC2626";
                  const rowBg = isCurrentRisk
                    ? "rgba(239, 68, 68, 0.05)"
                    : isFutureRisk
                      ? "rgba(245, 158, 11, 0.05)"
                      : "transparent";
                  return (
                  <tr
                    key={row.shipment_id || idx}
                    onClick={() => setSelectedRow(row)}
                    style={{ borderBottom: `1px solid ${BORDER_COLOR}`, cursor: "pointer", transition: "background-color 0.12s", backgroundColor: rowBg }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = BG_GREY; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = rowBg; }}
                  >
                    <td style={{ padding: "16px 24px", fontSize: "0.875rem", fontWeight: 600, color: TEXT_PRIMARY, whiteSpace: "nowrap", fontFamily: FONT }}>
                      {row.shipment_id || "—"}
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: "0.875rem", color: TEXT_PRIMARY, fontFamily: FONT }}>
                      {row.product_name || "—"}
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: "0.875rem", color: TEXT_PRIMARY, fontFamily: FONT }}>
                      {row.supplier_name || "—"}
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: "0.875rem", color: TEXT_PRIMARY, whiteSpace: "nowrap", fontFamily: FONT }}>
                      {row.location_name || "—"}
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: "0.875rem", color: TEXT_PRIMARY, whiteSpace: "nowrap", fontFamily: FONT }}>
                      {row.destination_location || "—"}
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: "0.875rem", color: delayColor, fontWeight: isFutureRisk ? 500 : 400, maxWidth: 240, overflow: "hidden", textOverflow: isFutureRisk ? "clip" : "ellipsis", whiteSpace: isFutureRisk ? "normal" : "nowrap", fontFamily: FONT }}>
                      {row.delay_reason ? (
                        isFutureRisk ? (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <Tooltip
                                title="This shipment is heading toward a HIGH risk port. It is currently moving normally but may be held when it arrives."
                                arrow
                              >
                                <span style={{ cursor: "help", lineHeight: 1 }}>⚠️</span>
                              </Tooltip>
                              <span>Upcoming: {row.delay_reason}</span>
                            </div>
                          </>
                        ) : row.delay_reason
                      ) : "-"}
                    </td>
                  </tr>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: "48px 24px", textAlign: "center", color: TEXT_MUTED, fontSize: "0.875rem", fontFamily: FONT }}>
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ── Pagination ── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", borderTop: `1px solid ${BORDER_COLOR}`, fontFamily: FONT, backgroundColor: "#fff" }}>
              <span style={{ fontSize: "0.875rem", color: TEXT_MUTED }}>
                {filteredRows.length === 0 ? "No results" : `${startRow}–${endRow} of ${filteredRows.length}`}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 6,
                    border: `1px solid ${BORDER_COLOR}`,
                    background: "#fff",
                    fontSize: "0.875rem",
                    color: page === 0 ? "#CBD5E1" : TEXT_PRIMARY,
                    cursor: page === 0 ? "not-allowed" : "pointer",
                    fontFamily: FONT,
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: "0.875rem", color: TEXT_MUTED, padding: "0 4px" }}>
                  Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 6,
                    border: `1px solid ${BORDER_COLOR}`,
                    background: "#fff",
                    fontSize: "0.875rem",
                    color: page >= totalPages - 1 ? "#CBD5E1" : TEXT_PRIMARY,
                    cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                    fontFamily: FONT,
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Shipment Details Popup ── */}
      <ShipmentDetailsPopup
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        order={selectedRow ? {
          order_id:    selectedRow.shipment_id || "",
          product:     selectedRow.product_name || "",
          destination: selectedRow.destination_location || selectedRow.location_name || "",
          status:      (selectedRow.display_status || selectedRow.current_ui_group || "in_progress").toLowerCase().replaceAll(/\s+/g, "_"),
          delay_days:  selectedRow.delay_days ?? "",
          notes:       selectedRow.delay_reason || "",
          ...selectedRow,
        } : null}
      />
    </div>
  );
};

export default OrdersOverview;
